import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api/api.js';
import { 
  Mail, 
  Trash2, 
  Calendar, 
  Search, 
  UserPlus, 
  Download,
  AlertCircle,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';
import { ConfirmDeleteModal } from '../../../shared/components/ConfirmDeleteModal.js';

export const NewsletterManagementView: React.FC = () => {
  const [search, setSearch] = useState('');
  const [deletingSub, setDeletingSub] = useState<{ id: string; email: string } | null>(null);
  const queryClient = useQueryClient();
  const showToast = useNotificationStore((state) => state.showToast);

  const { data: subscribers, isLoading, isError } = useQuery({
    queryKey: ['newsletter-subscribers'],
    queryFn: () => api.getNewsletterSubscribers(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteNewsletterSubscriber(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-subscribers'] });
      setDeletingSub(null);
      showToast('Berhasil', 'Pelanggan berhasil dihapus dari daftar', 'success');
    },
    onError: (error: any) => {
      setDeletingSub(null);
      showToast('Gagal', error.message || 'Gagal menghapus pelanggan', 'error');
    }
  });

  const handleDelete = (id: string, email: string) => {
    setDeletingSub({ id, email });
  };

  const filteredSubscribers = subscribers?.filter(sub => 
    sub.email.toLowerCase().includes(search.toLowerCase())
  ) || [];

  const handleExportCsv = () => {
    if (!subscribers || subscribers.length === 0) return;
    
    const headers = ['ID', 'Email', 'Tanggal Daftar'];
    const rows = subscribers.map(s => [s.id, s.email, s.createdAt]);
    const csvContent = [headers, ...rows].map(e => e.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `uibn_newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-5">
          <Mail className="w-32 h-32 text-indigo-600 rotate-12" />
        </div>
        
        <div className="relative z-10 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                Manajemen Newsletter
              </h2>
              <p className="text-sm text-slate-500">
                Kelola daftar email pelanggan yang terdaftar melalui portal blog UIBN.
              </p>
            </div>
            
            <button
              onClick={handleExportCsv}
              disabled={!subscribers || subscribers.length === 0}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-bold hover:bg-slate-800 transition shadow-sm disabled:opacity-50 cursor-pointer"
            >
              <Download className="w-4 h-4" />
              <span>Ekspor CSV</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4">
              <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1">Total Pelanggan</p>
              <p className="text-2xl font-black text-slate-900">{subscribers?.length || 0}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4">
              <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-1">Status Sistem</p>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <p className="text-sm font-bold text-slate-900">Aktif & Sinkron</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-xs">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input 
              type="text"
              placeholder="Cari email pelanggan..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="py-20 flex flex-col items-center justify-center gap-3 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              <p className="text-sm font-medium">Memuat data pelanggan...</p>
            </div>
          ) : isError ? (
            <div className="py-20 flex flex-col items-center justify-center gap-3 text-rose-500">
              <AlertCircle className="w-10 h-10" />
              <p className="text-sm font-bold">Gagal memuat data dari server</p>
            </div>
          ) : filteredSubscribers.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center gap-3 text-slate-400">
              <Mail className="w-12 h-12 opacity-20" />
              <p className="text-sm font-medium">Tidak ada pelanggan ditemukan</p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Email</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Tanggal Daftar</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscribers.map((sub) => (
                  <tr key={sub.id} className="border-b border-slate-50 hover:bg-slate-50/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-[10px] font-bold">
                          {sub.email.substring(0, 2).toUpperCase()}
                        </div>
                        <span className="text-sm font-semibold text-slate-900">{sub.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(sub.createdAt).toLocaleDateString('id-ID', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDelete(sub.id, sub.email)}
                        disabled={deleteMutation.isPending}
                        className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                        title="Hapus Pelanggan"
                      >
                        {deleteMutation.isPending && deleteMutation.variables === sub.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={Boolean(deletingSub)}
        title="Hapus Pelanggan Newsletter"
        description={deletingSub ? `Apakah Anda yakin ingin menghapus ${deletingSub.email} dari daftar newsletter?` : undefined}
        confirmText="Hapus Pelanggan"
        isPending={deleteMutation.isPending}
        onClose={() => setDeletingSub(null)}
        onConfirm={() => {
          if (deletingSub) {
            deleteMutation.mutate(deletingSub.id);
          }
        }}
      />
    </div>
  );
};
